import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SessionService } from './session.service';
import { acsChatEnvironment } from '../environments/environment';

/**
 * HTTP Interceptor for session-based authentication and validation.
 *
 * Responsibilities:
 * - Add session userId as query param or header to ACS chat API requests only
 * - Validate session is active before allowing ACS chat requests
 * - Handle 401 (Unauthorized) and 403 (Forbidden) responses
 *
 * Scoped: Only intercepts requests to the ACS chat API base URL.
 * Non-matching requests are passed through untouched.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private sessionService: SessionService) {}

  private isAcsChatRequest(url: string): boolean {
    const base = acsChatEnvironment.apiBaseUrl;
    // Match requests to the ACS chat backend (e.g. /api/users, /api/threads, etc.)
    return url.startsWith(base + '/') || url === base;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only intercept requests to the ACS chat API
    if (!this.isAcsChatRequest(req.url)) {
      return next.handle(req);
    }

    const userId = this.sessionService.getCurrentUserId();

    // Reject ACS chat requests without active session
    if (!userId) {
      console.warn('ACS chat API request blocked: No active session');
      return throwError(() => new Error('No active session. Please log in.'));
    }

    // Add userId as query param for Function App to validate
    const clonedReq = req.clone({
      setParams: {
        sessionUserId: userId,
      },
    });

    return next.handle(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          console.error('Session expired or invalid:', error.statusText);
          this.sessionService.endSession();
          return throwError(() => new Error('Session expired. Please log in again.'));
        }
        return throwError(() => error);
      })
    );
  }
}
